// Bildschirm "Kennungen" -- die Pflege externer Kennungen (ST-PTY-003,
// erster Schnitt; ADR-0047).
//
// EIN EIGENER BILDSCHIRM, dieselbe Entscheidung wie bei den Kontaktwegen:
// Kapitel 5.2 Nr. 2 verlangt EINE Hauptaktion je Bildschirm, und die dieses
// Bildschirms ist "eine Kennung hinzufuegen". Er hat eine eigene Adresse
// unter /kontakte/{partyId}/kennungen -- "schick mir die Kennungen der
// Buchhandlung" ist eine Bitte, die im Stewardship-Alltag vorkommt.
//
// PROGRESSIVE OFFENLEGUNG (5.2.4, die Story woertlich: "Identifikatoren
// folgen auf Anforderung"):
//   * Dieser Bildschirm existiert, WEIL das Kontaktdetail die Kennungen nicht
//     traegt -- sie erscheinen erst, wenn jemand sie oeffnet.
//   * Die Beendigung verlangt eine Bestaetigung, die erst auf Anforderung
//     erscheint -- eine folgenreiche Handlung faehrt nicht auf einen Klick.
//   * Die frueheren Kennungen liegen zugeklappt: sie sind der Beleg fuer
//     "beendet statt geloescht" (CM-PTY-004: historische IDs bleiben
//     aufloesbar), aber nicht die Arbeit.
//
// HERKUNFT IST SICHTBAR (Story-UX 5.2.9, "Show provenance"): jede Kennung
// nennt Aussteller, Erfassungsweg, Bestaetigungs- und Synchronisationsstand.
// Eine Handeingabe erscheint als "nicht bestaetigt" und "erfasst, nicht
// abgeglichen" -- ein Wert ohne benannte Quelle sieht nie wie ein
// bestaetigter aus.
//
// SICHERE FEHLERERHOLUNG (NFR-UX-005, CM-UX-004): vier Konfliktlagen, vier
// Saetze mit eigenem naechsten Schritt (identifiers.ts). Der Primaerkonflikt
// bekommt AUSDRUECKLICH keinen "trotzdem"-Knopf: der Server setzt nicht um,
// und ein Knopf, der es verspraeche, waere die Attrappe aus ADR-0002.
// Bricht beim Hinzufuegen die Verbindung ab, steht "Erneut versuchen" da:
// sicher, weil die Wiederholung dieselbe Kennung des Absendens traegt und der
// Server nichts doppelt anlegt (ADR-0060, client-request.ts).
//
// BARRIEREFREIHEIT (NFR-ACC-001): GENAU EINE Region mit aria-live; der Fokus
// wandert nach jedem Befehl dorthin; Zustaende stehen in Worten ("primär",
// "nicht bestätigt") und nie in Farbe allein; jeder Zeilenknopf traegt den
// Wert im Namen.
//
// DIE OBERFLAECHE ENTSCHEIDET NICHTS. Vokabular, Sperrstand, Primaerregel und
// Sichtbarkeit entscheidet der Server; dieser Bildschirm schickt und
// uebersetzt.

import { useState, type ReactElement } from 'react';
import { Badge, Body1, Button, Caption1, Field, Input, makeStyles, MessageBar, MessageBarBody, Radio, RadioGroup } from '@fluentui/react-components';

import {
  addIdentifier,
  additionalIdentifierNotice,
  confirmationFor,
  endIdentifier,
  fetchIdentifiers,
  HISTORY_TRUNCATED_NOTICE,
  identifierTypeLabel,
  issuerLabel,
  OFFERED_IDENTIFIER_TYPES,
  offeredIdentifierTypesFor,
  setIdentifierPrimary,
  SUBMIT_FAILED_MESSAGE,
  sourceSystemLabel,
  synchronizationLabel,
  validationMessageFor,
  type CommandResult,
  type EndedIdentifierView,
  type IdentifierView,
  type PartyIdentifiers,
} from '../identifiers.ts';
import { bestaetigungPilleSichtbar, formatInstant, verificationLabel } from '../parties.ts';
import { notWritableNotice } from '../party-lifecycle.ts';
import { Pflegeabschnitt, Pflegehistorie, Pflegeliste, usePflegeStyles, Zeilenaktionen } from '../shell/pflegeabschnitt.tsx';
import { AnlegenFormular, AnlegenOeffner, PflegeKopf, useAnlegen } from './pflege-kopf.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { useMaintainedSection } from '../shell/use-maintained-section.ts';

/**
 * Die Arten stehen breit nebeneinander und schmal untereinander: fuenf Arten
 * seit Signatur S2 passen bei 320 px nicht in eine Zeile, und ein Umbruch
 * ergaebe ungleiche Zeilen (Vorgabe DESIGN). Die Gruppe hat eine eigene
 * Schwelle statt der Schmalgrenze der Shell (48rem, layout.tsx): Die Reihe
 * braucht rund 573 px und ist bis etwa 860 px Fensterbreite breiter als das
 * Formular; ab 60rem passt sie gemessen (DESIGN).
 */
const useArtStyles = makeStyles({
  gruppe: {
    '@media (max-width: 60rem)': { flexDirection: 'column' },
  },
});

export interface KennungenScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
}

/**
 * Eine gueltige Kennung. Die Beendigung verlangt KEINEN Grund: fuer Kennungen
 * verlangt kein Requirement einen, und ein Pflichtfeld ohne Anforderung waere
 * erfundene Buerokratie (ADR-0047 Ziffer 6). Was bleibt, ist die Wirkung in
 * einem Satz VOR der Entscheidung.
 */
function Kennung({
  identifier,
  beendenOffen,
  busy,
  onBeendenOeffnen,
  onBeendenAbbrechen,
  onBeenden,
  onPrimary,
  nurLesen,
}: {
  readonly identifier: IdentifierView;
  readonly beendenOffen: boolean;
  readonly busy: boolean;
  readonly onBeendenOeffnen: () => void;
  readonly onBeendenAbbrechen: () => void;
  readonly onBeenden: () => void;
  readonly onPrimary: (isPrimary: boolean) => void;
  /** Der Kontakt ist nicht beschreibbar: auch der Primärstatus lässt sich nicht mehr ändern (Server: 409). */
  readonly nurLesen: boolean;
}): ReactElement {
  const own = usePflegeStyles();
  const art = identifierTypeLabel(identifier.identifierType);
  return (
    <li className={own.eintrag}>
      <Body1 className={own.wert}>{identifier.value}</Body1>
      <span className={own.merkmale}>
        {/* Eine Caption: ohne Pille dazwischen läsen sich Aussteller und Quelle sonst als ein Satz. */}
        <Caption1>
          {art}, ausgestellt von {issuerLabel(identifier.identifierType, identifier.issuer)} · {sourceSystemLabel(identifier.sourceSystem)} ·{' '}
          {synchronizationLabel(identifier.synchronizationStatus)}
        </Caption1>
        {identifier.isPrimary ? (
          // In WORTEN und nicht als Stern (NFR-ACC-001, WCAG 1.4.1).
          <Badge appearance="outline" color="brand">
            primär
          </Badge>
        ) : undefined}
        {bestaetigungPilleSichtbar(identifier.verificationStatus) ? (
          <Badge appearance="outline" color="informative">
            {verificationLabel(identifier.verificationStatus)}
          </Badge>
        ) : undefined}
      </span>

      <Zeilenaktionen
        name={identifier.value}
        offen={beendenOffen}
        busy={busy}
        aktion="Nicht mehr verwenden"
        frage="Diese Kennung nicht mehr verwenden?"
        frageId={`kn-beenden-${identifier.identifierId}`}
        wirkung="Sie wird nicht gelöscht. Sie bleibt unter „Frühere Kennungen“ nachlesbar und hält ab dann keinen Primärstatus mehr."
        bestaetigen="Nicht mehr verwenden"
        onOeffnen={onBeendenOeffnen}
        onAbbrechen={onBeendenAbbrechen}
        onBeenden={onBeenden}
      >
        {nurLesen ? undefined : (
          <>
        {identifier.isPrimary ? (
          <Button
            appearance="secondary"
            size="small"
            className={own.zeilenknopf}
            disabled={busy}
            aria-label={`Primärstatus entziehen: ${identifier.value}`}
            onClick={() => onPrimary(false)}
          >
            Primärstatus entziehen
          </Button>
        ) : (
          <Button
            appearance="secondary"
            size="small"
            className={own.zeilenknopf}
            disabled={busy}
            aria-label={`Als primär festlegen: ${identifier.value}`}
            onClick={() => onPrimary(true)}
          >
            Als primär festlegen
          </Button>
        )}
          </>
        )}
      </Zeilenaktionen>
    </li>
  );
}

function FruehereKennung({ entry }: { readonly entry: EndedIdentifierView }): ReactElement {
  const own = usePflegeStyles();
  return (
    <li className={own.eintrag}>
      <Body1 className={own.wert}>{entry.value}</Body1>
      <span className={own.merkmale}>
        <Caption1>
          {identifierTypeLabel(entry.identifierType)}, ausgestellt von {issuerLabel(entry.identifierType, entry.issuer)}
        </Caption1>
        <Caption1>nicht mehr verwendet seit {formatInstant(entry.endedAt)}</Caption1>
      </span>
    </li>
  );
}

export function KennungenScreen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
}: KennungenScreenProps): ReactElement {
  const styles = useScreenStyles();
  const artStyles = useArtStyles();
  const own = usePflegeStyles();
  const [art, setArt] = useState<string>(OFFERED_IDENTIFIER_TYPES[0]?.code ?? '');
  const [aussteller, setAussteller] = useState<string>(OFFERED_IDENTIFIER_TYPES[0]?.issuerSuggestion ?? '');
  const [wert, setWert] = useState('');
  const [beendenFuer, setBeendenFuer] = useState<string | undefined>(undefined);

  const abschnitt = useMaintainedSection<PartyIdentifiers>({
    accessToken,
    onReauthenticate,
    sectionKey: `${apiBaseUrl}|${partyId}`,
    fetchSection: (token) => fetchIdentifiers({ baseUrl: apiBaseUrl, accessToken: token, partyId }),
    createMessages: { submitFailed: SUBMIT_FAILED_MESSAGE },
  });
  const { busy, fieldErrors: feldfehler, submitFailed: absendenFehlgeschlagen } = abschnitt;
  // Der Lebenszyklus steht in der Antwort dieser Seite; fehlt er (aelteres Image) oder schlug ein spaeteres
  // Lesen fehl, gilt der zuletzt bekannte Stand, sonst bleibt das Angebot stehen und der Server lehnt mit seinem Satz ab.
  const nurLesen = notWritableNotice(abschnitt.data?.lifecycleStatus);
  const anlegen = useAnlegen(abschnitt.status === 'geladen' && abschnitt.data !== undefined && abschnitt.data.identifiers.length === 0, partyId, abschnitt.status === 'laedt');

  const befehl = (
    operation: 'end' | 'primary-set' | 'primary-unset',
    run: (token: string) => Promise<CommandResult>,
  ): void => {
    void abschnitt.command({
      run,
      confirm: (applied) => confirmationFor(operation, applied.identifierType),
      onApplied: () => setBeendenFuer(undefined),
    });
  };

  // Leitet der Server die ausstellende Stelle ab (USt-IdNr.), steht kein Feld dafuer da und
  // es geht nichts mit -- auch kein Rest aus einer vorher gewaehlten Art.
  const angebot = offeredIdentifierTypesFor(abschnitt.data?.kind);
  const gewaehlteArt = angebot.find((eintrag) => eintrag.code === art);
  const ausstellerAbgeleitet = gewaehlteArt?.issuerDerived === true;
  const ausstellerGesendet = ausstellerAbgeleitet ? '' : aussteller;

  /** Hinzufuegen -- aus dem Formular und aus "Erneut versuchen", mit derselben Absendekennung bei gleicher Eingabe. */
  const hinzufuegen = (): void => {
    void abschnitt.create({
      input: { identifierType: art, issuer: ausstellerGesendet, value: wert },
      run: (token, clientRequestId) =>
        addIdentifier({
          baseUrl: apiBaseUrl,
          accessToken: token,
          partyId,
          identifierType: art,
          issuer: ausstellerGesendet,
          value: wert,
          clientRequestId,
        }),
      confirm: (applied) => confirmationFor('add', applied.identifierType),
      clearInput: () => setWert(''),
      onApplied: () => {
        setBeendenFuer(undefined);
        anlegen.erledigt();
      },
    });
  };

  const artFehler = feldfehler.find((fehler) => fehler.fieldPath === 'identifierType');
  const wertFehler = feldfehler.find((fehler) => fehler.fieldPath === 'value');
  const ausstellerFehler = feldfehler.find((fehler) => fehler.fieldPath === 'issuer');

  return (
    <>
    <PflegeKopf partyId={partyId} name={abschnitt.data?.displayName} bereich="Kennungen" />
    <Pflegeabschnitt
      abschnitt={abschnitt}
      partyId={partyId}
      texte={{
        laden: 'Kennungen werden geladen',
        feldfehlerTitel: 'Die Kennung konnte nicht übernommen werden.',
        feldfehlerText: 'Prüfe die markierten Angaben und versuche es erneut.',
        neuLaden: 'Kennungen neu laden',
      }}
      // Steht das Formular zu (abgebrochen), oeffnet "Erneut versuchen" es zuerst.
      onErneutAnlegen={() => (anlegen.offen ? hinzufuegen() : anlegen.oeffnen())}
    >
      {(data) => (
        <>
          <Body1>Kennungen aus Registern und Fachsystemen.</Body1>
          {nurLesen !== undefined ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <strong>{nurLesen.headline}</strong> {nurLesen.explanation}
              </MessageBarBody>
            </MessageBar>
          ) : undefined}

          <Pflegeliste
            id="kn-gueltig"
            titel="Aktuelle Kennungen"
            leer="Für diesen Kontakt ist bisher keine Kennung hinterlegt."
            anzahl={data.identifiers.length}
            hinweis={additionalIdentifierNotice(data.additionalIdentifierCount)}
            aktion={nurLesen === undefined ? <AnlegenOeffner
                  steuerung={anlegen}
                  label="Kennung hinzufügen"
                  disabled={busy}
                  zuruecktreten={absendenFehlgeschlagen || abschnitt.retryCommand !== undefined || beendenFuer !== undefined}
                /> : undefined}
            vorListe={
              nurLesen === undefined ? (
                <AnlegenFormular
                  steuerung={anlegen}
                  name="Kennung hinzufügen"
                  className={styles.form}
                  onSenden={hinzufuegen}
                  absenden={
                    // Waehrend "Erneut versuchen" steht, tritt dieser Knopf zurueck -- genau eine Hauptaktion (NFR-UX-004).
                    <Button
                      appearance={absendenFehlgeschlagen ? 'secondary' : 'primary'}
                      type="submit"
                      disabled={busy || wert.trim().length === 0 || (!ausstellerAbgeleitet && aussteller.trim().length === 0)}
                    >
                      {busy ? 'Wird gespeichert …' : 'Kennung hinzufügen'}
                    </Button>
                  }
                >
                  <Field
                    label="Art der Kennung"
                    required
                    hint={gewaehlteArt?.hint ?? null}
                    validationMessage={artFehler === undefined ? null : validationMessageFor('identifierType', artFehler.reasonCode)}
                  >
                    <RadioGroup
                      id="kn-art"
                      layout="horizontal"
                      className={artStyles.gruppe}
                      value={art}
                      onChange={(_event, geaendert) => {
                        setArt(geaendert.value);
                        // Der Ausstellervorschlag folgt der Art -- aber nur, solange
                        // niemand etwas Eigenes eingetragen hat: ueberschrieben wird
                        // hoechstens ein anderer VORSCHLAG, nie eine Eingabe.
                        const vorher = OFFERED_IDENTIFIER_TYPES.find((eintrag) => eintrag.code === art);
                        const naechster = OFFERED_IDENTIFIER_TYPES.find((eintrag) => eintrag.code === geaendert.value);
                        if (aussteller === '' || aussteller === (vorher?.issuerSuggestion ?? '')) {
                          setAussteller(naechster?.issuerSuggestion ?? '');
                        }
                      }}
                    >
                      {angebot.map((eintrag) => (
                        <Radio key={eintrag.code} value={eintrag.code} label={eintrag.label} />
                      ))}
                    </RadioGroup>
                  </Field>
                  {ausstellerAbgeleitet ? undefined : (
                    <Field
                      label="Ausstellende Stelle"
                      required
                      validationMessage={
                        ausstellerFehler === undefined ? null : validationMessageFor('issuer', ausstellerFehler.reasonCode)
                      }
                    >
                      <Input
                        id="kn-aussteller"
                        className={own.feld}
                        value={aussteller}
                        onChange={(_event, geaendert) => setAussteller(geaendert.value)}
                      />
                    </Field>
                  )}
                  <Field
                    label="Wert der Kennung"
                    required
                    validationMessage={wertFehler === undefined ? null : validationMessageFor('value', wertFehler.reasonCode, art)}
                  >
                    <Input
                      id="kn-wert"
                      className={own.feld}
                      value={wert}
                      onChange={(_event, geaendert) => setWert(geaendert.value)}
                    />
                  </Field>
                </AnlegenFormular>
              ) : undefined
            }
          >
            {data.identifiers.map((identifier) => (
              <Kennung
                key={identifier.identifierId}
                identifier={identifier}
                nurLesen={nurLesen !== undefined}
                busy={busy}
                beendenOffen={beendenFuer === identifier.identifierId}
                onBeendenOeffnen={() => setBeendenFuer(identifier.identifierId)}
                onBeendenAbbrechen={() => setBeendenFuer(undefined)}
                onBeenden={() =>
                  befehl('end', (token) =>
                    endIdentifier({
                      baseUrl: apiBaseUrl,
                      accessToken: token,
                      partyId,
                      identifierId: identifier.identifierId,
                      lockVersion: identifier.lockVersion,
                    }),
                  )
                }
                onPrimary={(isPrimary) =>
                  befehl(isPrimary ? 'primary-set' : 'primary-unset', (token) =>
                    setIdentifierPrimary({
                      baseUrl: apiBaseUrl,
                      accessToken: token,
                      partyId,
                      identifierId: identifier.identifierId,
                      isPrimary,
                      lockVersion: identifier.lockVersion,
                    }),
                  )
                }
              />
            ))}
          </Pflegeliste>

          <Pflegehistorie
            titel="Frühere Kennungen"
            anzahl={data.endedIdentifiers.length}
            leer="Bisher gibt es keine früheren Kennungen."
            abgeschnitten={data.endedIdentifiersTruncated ? HISTORY_TRUNCATED_NOTICE : undefined}
          >
            {data.endedIdentifiers.map((entry) => (
              <FruehereKennung key={entry.identifierId} entry={entry} />
            ))}
          </Pflegehistorie>
        </>
      )}
    </Pflegeabschnitt>
    </>
  );
}
